/**
 * Dashboard.jsx — Pantalla principal de gestión de bots
 *
 * Muestra el listado de bots del usuario con opciones para
 * crear, abrir, renombrar, cambiar teléfono y eliminar.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { listBots, getBot, createBot, updateBotMeta, deleteBot, duplicateBot, saveFlow } from "./storage";
import JSZip from "jszip";

export default function Dashboard({ userId, displayName, avatarLetter, provider, onLogout, onOpenBot }) {
  const [bots, setBots]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Modal nuevo bot
  const [showNew, setShowNew]     = useState(false);
  const [newName, setNewName]     = useState("");
  const [newPhone, setNewPhone]   = useState("");
  const [creating, setCreating]   = useState(false);

  // Edición inline
  const [editingId, setEditingId]     = useState(null);
  const [editName, setEditName]       = useState("");
  const [editPhone, setEditPhone]     = useState("");

  // Confirmación de borrado
  const [deletingId, setDeletingId]   = useState(null);

  // Abriendo bot (carga el data completo antes de navegar)
  const [openingId, setOpeningId]     = useState(null);

  // Importar Bot CyT
  const cytFileRef = useRef(null);
  const [cytMsg, setCytMsg] = useState(null); // { ok, text }
  const [cytConfigOpen, setCytConfigOpen] = useState(false);
  const [groqEnabled, setGroqEnabled]     = useState(false);
  const [groqConfig, setGroqConfig]       = useState({
    apiKey: "", model: "llama-3.1-8b-instant",
    systemPrompt: "", exitKeyword: "salir", maxTokens: 500,
  });

  // Modal de conversión en tiempo real
  const [convOpen, setConvOpen]   = useState(false);
  const [convLog,  setConvLog]    = useState([]);  // [{ msg, type }]
  const [convDone, setConvDone]   = useState(false);
  const convLogRef  = useRef([]);
  const logScrollRef = useRef(null);

  useEffect(() => {
    if (logScrollRef.current) {
      logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight;
    }
  }, [convLog]);

  // Config IA por bot
  const [aiConfigBot, setAiConfigBot]   = useState(null);
  const [aiConfigData, setAiConfigData] = useState({ apiKey: "", model: "llama-3.1-8b-instant", systemPrompt: "", exitKeyword: "salir", endPhrase: "", maxTokens: 500, knowledgeDocs: [] });
  const [aiConfigSaving, setAiConfigSaving] = useState(false);
  const [aiConfigLoading, setAiConfigLoading] = useState(false);
  const docFileRef = useRef(null);

  const handleOpenAiConfig = async (bot) => {
    setAiConfigLoading(true);
    setAiConfigBot(bot);
    try {
      const full = await getBot(bot.id);
      setAiConfigData({ apiKey: "", model: "llama-3.1-8b-instant", systemPrompt: "", exitKeyword: "salir", endPhrase: "", maxTokens: 500, knowledgeDocs: [], ...(full?.data?.aiConfig || {}) });
    } catch (e) { setError(e.message); setAiConfigBot(null); }
    finally { setAiConfigLoading(false); }
  };

  const handleAddDocs = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const content = ev.target.result;
        setAiConfigData(c => ({
          ...c,
          knowledgeDocs: [...(c.knowledgeDocs || []), { name: file.name, content }],
        }));
      };
      reader.readAsText(file, "utf-8");
    });
  };

  const handleRemoveDoc = (idx) =>
    setAiConfigData(c => ({ ...c, knowledgeDocs: c.knowledgeDocs.filter((_, i) => i !== idx) }));

  const handleSaveAiConfig = async () => {
    if (!aiConfigBot) return;
    setAiConfigSaving(true);
    try {
      const full = await getBot(aiConfigBot.id);
      await saveFlow(aiConfigBot.id, { ...(full?.data || {}), aiConfig: aiConfigData });
      setAiConfigBot(null);
    } catch (e) { setError(e.message); }
    finally { setAiConfigSaving(false); }
  };

  // Copia de bot
  const [copySource, setCopySource]   = useState(null); // bot que se va a copiar
  const [copyName, setCopyName]       = useState("");
  const [copyPhone, setCopyPhone]     = useState("");
  const [copying, setCopying]         = useState(false);

  const handleOpen = async (bot) => {
    setOpeningId(bot.id);
    try {
      const fullBot = await getBot(bot.id);
      onOpenBot(fullBot);
    } catch (e) {
      setError(e.message);
    } finally {
      setOpeningId(null);
    }
  };

  const handleDuplicate = async () => {
    if (!copyName.trim() || !copySource) return;
    setCopying(true);
    try {
      const newBot = await duplicateBot(userId, copySource.id, copyName.trim(), copyPhone.trim());
      setCopySource(null); setCopyName(""); setCopyPhone("");
      onOpenBot(newBot); // abre el bot duplicado directo
    } catch (e) { setError(e.message); }
    finally { setCopying(false); }
  };

  // ── Convertir Bot CyT → JSON descargable ──────────────────
  const downloadConvLog = () => {
    const text = convLogRef.current.map(e => `[${e.type.toUpperCase()}] ${e.msg}`).join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "cyt_conversion.log";
    a.click();
  };

  const handleConvertCyt = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setCytMsg(null);

    // Abrir modal y resetear log
    convLogRef.current = [];
    setConvLog([]);
    setConvDone(false);
    setConvOpen(true);

    const L = (msg, type) => {
      const t = type || (msg.startsWith("ERROR") || msg.startsWith("EXCEPTION") ? "error"
        : msg.startsWith("WARN") ? "warn"
        : msg.startsWith("---") ? "section"
        : "info");
      const entry = { msg, type: t };
      convLogRef.current = [...convLogRef.current, entry];
      setConvLog(prev => [...prev, entry]);
    };

    L(`ZIP recibido: ${file.name}`);

    const NODE_TYPE_MAP = {
      NODO_OBTENER_CAUSA_CONEXION: "startingPoint",
      NODO_USSD: "textMenu",
      NODO_CONFIGURADOR_BOTONES: "listButtons",
      NODO_INTEGRACION_BOT: "aiBot",
      NODO_COMPARAR_VARIABLES: "conditions",
      NODO_CONSULTAR_BASE: "dbQuery",
      NODO_ASIGNA_VARIABLE: "setField",
      NODO_EMITIR_MENSAJE: "sendMessage",
      NODO_TRANSFIERE_ACD: "transferAgent",
      NODO_CONT_CICLO_REPETITIVO: "loopControl",
      NODO_LIBERAR_LINEA: "goodbye",
      NODO_INTENCIONES: "setField",        // guarda intención como variable interna
      NODO_CODIGOS_FINALIZACION: "setField", // guarda código de finalización
      NODO_PREFERENCIA_ATENCION: "acdSkill",
      NODO_ETIQUETA_GENERAL_TITLE: null,
      NODO_ETIQUETA_COMENTARIO: null,      // nodo visual amarillo, sin lógica
    };

    const PORT_MAP = {
      "Verdadero": "true", "Falso": "false",
      "Acceso válido": "success", "Error en consulta": "error",
      "No hay registros": "error", "Requiere asistencia humana": "needsHuman",
      "Finaliza OK": "ok", "Desconexión": "disconnect",
      "No hay operadores": "noOperators", "Fin de mensaje": "default",
      "Variable asignada": "default", "Texto": "default",
      "Ciclo completo": "complete", "Ciclo incomp.": "incomplete",
      "No Ingresó": "timeout", "Entrada Inválida": "invalid",
      "Entrada Válida": "invalid",
      "Detectó tag extra": "tagDetected", "Marcó dígito finalizador": "digitEnd",
      "Error - Sin recursos": "noResources", "Error": "error",
      // Nuevos — CONFIGURADOR_BOTONES / INTENCIONES / CODIGOS
      "Boton 1": "btn_0", "Boton 2": "btn_1", "Boton 3": "btn_2",
      "Lista 1": "list_1", "Lista 2": "list_2", "Lista 3": "list_3",
      "Lista 4": "list_4", "Lista 5": "list_5", "Lista 6": "list_6",
      "Lista 7": "list_7", "Lista 8": "list_8", "Lista 9": "list_9",
      "Lista 10": "list_10",
      "Salida por error": "error", "Salida por timeout": "timeout",
      "Intenciones": "default", "Código Finalizacion Asignado": "default",
      "Codigo Finalizacion Asignado": "default", "Próximo nodo": "default",
    };

    // Resuelve UNA etiqueta a un puerto
    const resolveOneLabel = (lbl) => {
      if (PORT_MAP[lbl] !== undefined) return PORT_MAP[lbl];
      const mDtmf = lbl.match(/^(\d+)\s*-/);
      if (mDtmf) return `tm_${mDtmf[1]}`;
      const mList = lbl.match(/^Lista (\d+)$/i);
      if (mList) return `list_${mList[1]}`;
      const mBtn = lbl.match(/^Bot[oó]n\s*(\d+)$/i);
      if (mBtn) return `btn_${parseInt(mBtn[1]) - 1}`; // CyT 1-based → designer 0-based
      const lo = lbl.toLowerCase();
      if (lo.includes("desconex")) return "disconnect";
      if (lo.includes("finaliza")) return "ok";
      if (lo.includes("asistencia")) return "needsHuman";
      if (lo.includes("operador")) return "noOperators";
      if (lo.includes("registros")) return "error";
      if (lo.includes("válido") || lo.includes("valido")) return "success";
      if (lo.includes("verdad")) return "true";
      if (lo.includes("falso")) return "false";
      if (lo.includes("completo")) return "complete";
      if (lo.includes("incompl")) return "incomplete";
      if (lo.includes("inválid") || lo.includes("invalido")) return "invalid";
      if (lo.includes("ingres")) return "timeout";
      if (lo.includes("tag")) return "tagDetected";
      if (lo.includes("dígit") || lo.includes("digit")) return "digitEnd";
      if (lo.includes("recurso")) return "noResources";
      if (lo.includes("intencion") || lo.includes("intención")) return "default";
      if (lo.includes("finalizaci")) return "default";
      if (lo.includes("próximo") || lo.includes("proximo")) return "default";
      if (lo.includes("salida por error")) return "error";
      if (lo.includes("salida por timeout")) return "timeout";
      return null;
    };

    // Expande etiquetas separadas por coma → array de puertos únicos
    // Un link CyT con "A, B, C" → [spA, spB, spC] deduplicados
    const resolvePorts = (rawLabel) => {
      if (!rawLabel) return ["default"];
      const ports = [];
      for (const lbl of rawLabel.split(",").map(s => s.trim())) {
        const p = resolveOneLabel(lbl);
        if (p && !ports.includes(p)) ports.push(p);
      }
      return ports.length ? ports : ["default"];
    };

    // ── Helpers de conversión de variables CyT ────────────────
    // Convierte %VAR y [%VAR.PROP] en texto → {{VAR}} / {{VAR_PROP}}
    const toText = s => {
      if (!s || s === "NULL") return "";
      return s
        .replace(/\[%([A-Z_][A-Z0-9_.]*)\]/g, (_, v) => `{{${v.replace(/\./g, "_")}}}`)
        .replace(/%([A-Z_][A-Z0-9_.]*)/g, "{{$1}}");
    };
    // Extrae nombre simple de variable: %STS→STS, [%CTIME.SAL]→CTIME_SAL
    const toVarName = s => {
      if (!s || s === "NULL") return "";
      return s.replace(/^\[?%?/, "").replace(/\]$/, "").replace(/\./g, "_");
    };

    const buildData = (extType, ent) => {
      const p = ent || {};
      switch (extType) {
        case "NODO_INTEGRACION_BOT": {
          const slm = (p.prop15 || "").match(/sleeptime=(\d+)/i);
          // outputVariable: si es expresión compleja (contiene paréntesis), descartar
          const rawOut = toVarName(p.prop16);
          const outputVariable = /[()[\]]/.test(rawOut) ? "" : rawOut;
          return {
            assistantName:   p.prop8  || "",
            welcomeThread:   p.prop2  || "",
            outputVariable,
            disconnectVar:   toVarName(p.prop3),
            assistanceVar:   toVarName(p.prop4),
            tagAdicional:    p.prop5  || "",
            timeout:         Number(p.prop9)  || 250,
            confidenceLevel: Number(p.prop10) || 0,
            escapeDigit:     p.prop13 || "#",
            advancedConfig:  p.prop15 || "",
            sleepTime:       slm ? Number(slm[1]) : 750,
          };
        }
        case "NODO_USSD":
          return {
            message:         toText(p.prop0),
            retryTimeoutMsg: toText(p.prop1),
            retryErrorMsg:   toText(p.prop2),
            minDigits:       Number(p.prop3) || 1,
            maxDigits:       Number(p.prop4) || 20,
            options:         [],
            retries:         Number(p.prop6) || 0,
            timeoutSecs:     Number(p.prop7) || 900,
            saveResponseTo:  (p.prop12 && p.prop12 !== "NO") ? toVarName(p.prop12) : "",
          };
        case "NODO_COMPARAR_VARIABLES": {
          // prop0=variable, prop1=VALOR, prop2=CÓDIGO OPERADOR (estaban invertidos)
          const opMap = { "0": "equals", "1": "less_than", "2": "greater_than",
                          "3": "less_than", "4": "greater_than", "6": "not_equals" };
          const op = opMap[String(p.prop2 ?? "0")] || "equals";
          return {
            conditions: [{ variable: toVarName(p.prop0), operator: op, value: String(p.prop1 ?? "") }],
            logicOp: "AND",
          };
        }
        case "NODO_CONSULTAR_BASE": {
          // prop0 = connection string (DSN=...;UID=...;PWD=...)
          // prop1 = tipo: 1=select, 2=insert, 3=update, 4=procedure
          // prop2 = nombre del procedimiento/tabla
          // prop3 = parámetros de entrada
          // prop4..prop20 = campos de salida en formato "T@NOMBRE@" (T=tipo, NOMBRE=campo)
          const QUERY_TYPE_MAP = { "1": "select", "2": "insert", "3": "update", "4": "procedure" };
          const queryType = QUERY_TYPE_MAP[String(p.prop1)] || "procedure";

          // Recolectar todos los campos de salida (prop4 en adelante que tengan @nombre@)
          const outputFields = [];
          const FIELD_TYPE_MAP = { "S": "string", "N": "number", "D": "date", "B": "boolean" };
          for (let i = 4; i <= 20; i++) {
            const raw = p[`prop${i}`] || "";
            const m = raw.match(/^([A-Z])@([^@]+)@/);
            if (!m) continue;
            outputFields.push({ type: FIELD_TYPE_MAP[m[1]] || "string", name: m[2] });
          }

          // saveResponseTo = primer campo para compatibilidad con el panel actual
          const saveResponseTo = outputFields[0]?.name || "";

          return {
            dbType: "sqlserver",  // DSN indica SQL Server (no postgres)
            connectionString: p.prop0 || "",
            queryType,
            query: p.prop2 || "",
            params: p.prop3 || "",
            outputFields,
            saveResponseTo,
          };
        }
        case "NODO_ASIGNA_VARIABLE":
          return { variable: toVarName(p.prop0), value: toText(p.prop1) };
        case "NODO_EMITIR_MENSAJE":
          return { message: toText(p.prop0) };
        case "NODO_TRANSFIERE_ACD":
          // prop0=priority (1=high), prop1=queue id
          return { queue: p.prop1 || "", priority: p.prop0 === "1" ? "high" : "normal" };
        case "NODO_CONT_CICLO_REPETITIVO":
          return { variable: toVarName(p.prop0) || "BUCLE", maxCycles: Number(p.prop1) || 3 };
        case "NODO_LIBERAR_LINEA":
          return { message: "" };
        case "NODO_CONFIGURADOR_BOTONES":
          // prop2=body, prop3=reintento error, prop4=reintento timeout
          // El tipo real (replyButtons vs listButtons) se determina por los links salientes.
          // buildData retorna una base compatible con ambos; las opciones/botones se asignan después.
          return {
            header:      "",
            body:        toText(p.prop2),
            footer:      toText(p.prop4),
            buttonText:  "Ver opciones",
            buttons:     [],                         // para replyButtons
            sections:    [{ title: "", options: [] }], // para listButtons
            saveResponseTo: "",
          };
        case "NODO_PREFERENCIA_ATENCION": {
          // prop0..propN = habilidades disponibles; se importan todas como lista
          const skills = [];
          for (let i = 0; i <= 20; i++) {
            const v = p[`prop${i}`];
            if (v && v !== "NULL" && v.trim()) skills.push(v.trim());
          }
          return { skill: skills[0] || "", skills };
        }
        case "NODO_INTENCIONES":
          // Guarda IDs de intención como variables internas (metadata CRM)
          return { variable: "INTENCION", value: `${p.prop0 || ""}:${p.prop1 || ""}` };
        case "NODO_CODIGOS_FINALIZACION":
          // Guarda código de finalización
          return { variable: "COD_FIN", value: p.prop0 || "" };
        default:
          return {};
      }
    };

    // Identifica cada archivo por su clave raíz, no por nombre
    const identifyFiles = (results) => {
      let nodosArr = null, linksArr = null, datosMap = null, metaObj = null;
      for (const { fileName, parsed } of results) {
        const keys = Object.keys(parsed);
        L(`  ${fileName} → claves raíz: ${keys.join(", ")}`);
        if (parsed.nodos !== undefined)           { nodosArr = parsed.nodos;           L(`  → identificado como NODOS (${parsed.nodos.length} items)`); }
        if (parsed.links !== undefined)           { linksArr = parsed.links;           L(`  → identificado como LINKS (${parsed.links.length} items)`); }
        if (parsed.datosNodosLinks !== undefined) { datosMap = parsed.datosNodosLinks; L(`  → identificado como DATOS (${Object.keys(parsed.datosNodosLinks).length} entries)`); }
        if (parsed.metadataDiagrama !== undefined){ metaObj  = parsed.metadataDiagrama; L(`  → identificado como METADATA`); }
      }
      return { nodosArr, linksArr, datosMap, metaObj };
    };

    JSZip.loadAsync(file)
      .then(zip => {
        const jsonFiles = Object.values(zip.files).filter(f => !f.dir && f.name.endsWith(".json"));
        if (!jsonFiles.length) {
          L("ERROR: el ZIP no contiene archivos JSON", "error");
          setConvDone(true); setCytMsg({ ok: false, text: "❌ ZIP sin archivos JSON." });
          return Promise.reject("no-json");
        }
        L(`JSON encontrados en ZIP: ${jsonFiles.map(f => f.name).join(", ")}`);
        return Promise.all(jsonFiles.map(f =>
          f.async("string").then(text => {
            try { return { fileName: f.name, parsed: JSON.parse(text) }; }
            catch (err) { throw new Error(`${f.name}: JSON inválido — ${err.message}`); }
          })
        ));
      })
      .then((results) => {
        if (!results) return;
        L("--- Identificación de archivos ---");
        const { nodosArr, linksArr, datosMap, metaObj } = identifyFiles(results);

        if (!nodosArr) { L("ERROR: no se encontró archivo con clave 'nodos'", "error"); setConvDone(true); setCytMsg({ ok: false, text: "❌ Falta archivo de nodos." }); return; }
        if (!linksArr) { L("ERROR: no se encontró archivo con clave 'links'", "error"); setConvDone(true); setCytMsg({ ok: false, text: "❌ Falta archivo de links." }); return; }
        if (!datosMap) { L("ERROR: no se encontró archivo con clave 'datosNodosLinks'", "error"); setConvDone(true); setCytMsg({ ok: false, text: "❌ Falta archivo de datos." }); return; }

        const camName = metaObj?.campania?.nombre || "Bot importado";
        L(`--- Convirtiendo flujo: "${camName}" ---`);
        L(`Nodos externos: ${nodosArr.length}`);
        L(`Links externos: ${linksArr.length}`);

        const nodeIds = new Set(nodosArr.map(n => n.id).filter(Boolean));

        // Mapa de link entries (tipo:"esUnLink") → para extraer keywords desde salidas[N].comments
        const linkKwMap = {};
        for (const [id, entry] of Object.entries(datosMap)) {
          if (entry.tipo === "esUnLink") linkKwMap[id] = entry;
        }

        // Pre-scan: determinar si cada NODO_CONFIGURADOR_BOTONES es replyButtons o listButtons
        // según los labels de sus links salientes
        const btnNodeTypes = {}; // nodeId → "replyButtons" | "listButtons"
        for (const lk of linksArr) {
          const mBtn  = lk.text?.match(/^Bot[oó]n\s*(\d+)$/i);
          const mList = lk.text?.match(/^Lista (\d+)$/i);
          if (mBtn  && lk.origin && !btnNodeTypes[lk.origin]) btnNodeTypes[lk.origin] = "replyButtons";
          if (mList && lk.origin) btnNodeTypes[lk.origin] = "listButtons"; // lista tiene precedencia
        }

        // Inferir opciones textMenu / listButtons / replyButtons desde links salientes
        const menuOpts = {};
        for (const lk of linksArr) {
          // textMenu: "N - X" → salidas[DTMF].comments
          const mDtmf = lk.text?.match(/^(\d+)\s*-\s*(\S+)/);
          if (mDtmf && lk.origin) {
            if (!menuOpts[lk.origin]) menuOpts[lk.origin] = [];
            const dtmf = parseInt(mDtmf[1]);
            const linkEntry = linkKwMap[lk.id];
            const keyword = linkEntry?.salidas?.[dtmf]?.comments || mDtmf[2];
            const destName = datosMap[lk.destination]?.textoVisible || `Opción ${dtmf}`;
            menuOpts[lk.origin].push({ id: `tm_${dtmf}`, keyword, label: destName });
            L(`  textMenu opt ${lk.origin.substring(0,8)}: DTMF=${dtmf} keyword="${keyword}"`);
          }
          // listButtons: "Lista N" → salidas[N+2].comments
          const mList = lk.text?.match(/^Lista (\d+)$/i);
          if (mList && lk.origin) {
            if (!menuOpts[lk.origin]) menuOpts[lk.origin] = [];
            const listN = parseInt(mList[1]);
            const linkEntry = linkKwMap[lk.id];
            const title = linkEntry?.salidas?.[listN + 2]?.comments || `Opción ${listN}`;
            menuOpts[lk.origin].push({ _listN: listN, title, desc: "" });
            L(`  listButtons opt ${lk.origin.substring(0,8)}: Lista=${listN} title="${title}"`);
          }
          // replyButtons: "Boton N" → salidas[N-1].comments (0-based index en salidas)
          const mBtn = lk.text?.match(/^Bot[oó]n\s*(\d+)$/i);
          if (mBtn && lk.origin) {
            if (!menuOpts[lk.origin]) menuOpts[lk.origin] = [];
            const btnN = parseInt(mBtn[1]); // 1-based
            const linkEntry = linkKwMap[lk.id];
            const label = linkEntry?.salidas?.[btnN - 1]?.comments
              || datosMap[lk.destination]?.textoVisible
              || `Botón ${btnN}`;
            menuOpts[lk.origin].push({ _btnN: btnN, label });
            L(`  replyButtons opt ${lk.origin.substring(0,8)}: Boton=${btnN} label="${label}"`);
          }
        }

        // Ordenar opciones por índice numérico y asignar ids finales
        for (const nodeId of Object.keys(menuOpts)) {
          const opts = menuOpts[nodeId];
          if (opts[0]?.id) {
            // textMenu: ya tiene id (tm_N)
            opts.sort((a, b) => parseInt(a.id.replace(/\D/g, "")) - parseInt(b.id.replace(/\D/g, "")));
            L(`  textMenu ${nodeId.substring(0,8)}: ${opts.map(o=>`${o.id}=${o.keyword}`).join(", ")}`);
          } else if (opts[0]?._listN !== undefined) {
            // listButtons: tiene _listN → asignar opt_0, opt_1…
            opts.sort((a, b) => a._listN - b._listN);
            opts.forEach((o, i) => { o.id = `opt_${i}`; });
            L(`  listButtons ${nodeId.substring(0,8)}: ${opts.map(o=>`${o.id}=${o.title}`).join(", ")}`);
          } else if (opts[0]?._btnN !== undefined) {
            // replyButtons: tiene _btnN → asignar btn_0, btn_1…
            opts.sort((a, b) => a._btnN - b._btnN);
            opts.forEach((o, i) => { o.id = `btn_${i}`; });
            L(`  replyButtons ${nodeId.substring(0,8)}: ${opts.map(o=>`${o.id}=${o.label}`).join(", ")}`);
          }
        }

        // Mapa list_N → opt_N para remap de edges (solo listButtons)
        const listPortMap = {};
        for (const nodeId of Object.keys(menuOpts)) {
          const opts = menuOpts[nodeId];
          if (!opts[0]?.id?.startsWith("opt_")) continue;
          listPortMap[nodeId] = {};
          opts.forEach((o, i) => { listPortMap[nodeId][`list_${i + 1}`] = o.id; });
        }

        L("--- Traducción de nodos ---");
        const nodes = [];
        for (const n of nodosArr) {
          if (!n.id) { L(`  SKIP nodo sin id: ${JSON.stringify(n)}`); continue; }
          let mapped = NODE_TYPE_MAP[n.nodeType];
          // NODO_CONFIGURADOR_BOTONES: determinar tipo real según sus links salientes
          if (n.nodeType === "NODO_CONFIGURADOR_BOTONES") {
            mapped = btnNodeTypes[n.id] || "replyButtons";
          }
          if (mapped === null) { L(`  SKIP ${n.nodeType} (nodo de título)`); continue; }
          if (mapped === undefined) { L(`  WARN tipo desconocido: ${n.nodeType} → sendMessage`); }
          const type = mapped || "sendMessage";
          const ent  = datosMap[n.id]?.entrada;
          if (!ent) L(`  WARN sin datos para ${n.id} (${n.nodeType})`);
          const data = buildData(n.nodeType, ent);
          if (type === "textMenu" && menuOpts[n.id]) {
            data.options = menuOpts[n.id];
          }
          if (type === "listButtons" && menuOpts[n.id]) {
            data.sections = [{ title: "", options: menuOpts[n.id] }];
          }
          if (type === "replyButtons" && menuOpts[n.id]) {
            data.buttons = menuOpts[n.id].map(o => ({ id: o.id, label: o.label }));
          }
          L(`  OK ${n.id} → ${type} (x:${n.x * 10}, y:${n.y * 10})`);
          nodes.push({ id: n.id, type, x: n.x * 10, y: n.y * 10, data });
        }

        L("--- Traducción de edges ---");
        const seenEdges = new Set();
        const edges = [];
        for (const lk of linksArr) {
          if (!lk.origin || !lk.destination) { L(`  SKIP link sin origin/destination: ${lk.id}`); continue; }
          if (!nodeIds.has(lk.origin))      { L(`  SKIP link ${lk.id}: origin desconocido ${lk.origin}`); continue; }
          if (!nodeIds.has(lk.destination)) { L(`  SKIP link ${lk.id}: destination desconocida ${lk.destination}`); continue; }
          if (seenEdges.has(lk.id))         { L(`  SKIP link duplicado: ${lk.id}`); continue; }
          seenEdges.add(lk.id);
          const ports = resolvePorts(lk.text || "");
          for (let i = 0; i < ports.length; i++) {
            let sp = ports[i];
            // Remapear list_N → opt_N según el orden real de opciones del nodo
            if (sp.startsWith("list_") && listPortMap[lk.origin]) {
              sp = listPortMap[lk.origin][sp] || sp;
            }
            const edgeId = ports.length === 1 ? `e_${lk.id}` : `e_${lk.id}_${i}`;
            edges.push({ id: edgeId, src: lk.origin, sp, tgt: lk.destination, tp: "input" });
          }
          L(`  OK ${lk.origin} →[${lk.text}]→ ${lk.destination}  sp="${ports.join(",")}"`);
        }

        // ── Inyectar sendMessage después de cada transferAgent ───
        // Por cada nodo transferAgent, insertar un sendMessage con el texto
        // "Transferido a agente" conectado al puerto "transferred" como marcador.

        // Dimensiones aproximadas de nodo (NW=240 del canvas, alturas típicas)
        const INJ_NW = 240;
        const INJ_GAP = 60; // espacio horizontal entre nodos

        // Zona de inyección: fila propia debajo de todos los nodos CyT
        const injBaseY = Math.max(...nodes.map(n => n.y)) + 300;

        // Devuelve true si el rectángulo (x,y) colisiona con algún nodo de la lista
        const collides = (x, y, nodeList) =>
          nodeList.some(n => {
            return x < n.x + INJ_NW + INJ_GAP && x + INJ_NW + INJ_GAP > n.x &&
                   y < n.y + 200      + INJ_GAP && y + 200      + INJ_GAP > n.y;
          });

        // Busca el primer x libre en injBaseY a partir de candidateX
        const findFreeX = (candidateX, nodeList) => {
          let x = candidateX;
          while (collides(x, injBaseY, nodeList)) x += INJ_NW + INJ_GAP;
          return x;
        };

        // Ordenar por X para que las cadenas queden en orden visual de izquierda a derecha
        const transferNodes = nodes
          .filter(n => n.type === "transferAgent")
          .sort((a, b) => a.x - b.x);

        for (const tn of transferNodes) {
          const smId = `sm_post_${tn.id}`;
          // Partir de la X del transferAgent, en la fila de inyección
          const smX = findFreeX(tn.x, nodes);
          nodes.push({
            id: smId, type: "sendMessage",
            x: smX, y: injBaseY,
            data: { message: "Transferido a agente" },
          });
          edges.push({ id: `e_post_${tn.id}`, src: tn.id, sp: "transferred", tgt: smId, tp: "input" });
          L(`  +sendMessage post-transferAgent: ${tn.id} → ${smId} (x:${smX}, y:${injBaseY})`, "ok");

          if (groqEnabled) {
            const gcId = `gc_post_${tn.id}`;
            const gcX = findFreeX(smX + INJ_NW + INJ_GAP, nodes);
            nodes.push({
              id: gcId, type: "groqChat",
              x: gcX, y: injBaseY,
              data: { outputVariable: "" },
            });
            edges.push({ id: `e_gc_${tn.id}`, src: smId, sp: "default", tgt: gcId, tp: "input" });
            L(`  +groqChat post-sendMessage: ${smId} → ${gcId} (x:${gcX}, y:${injBaseY})`, "ok");
          }
        }

        L(`--- Resultado: ${nodes.length} nodos, ${edges.length} edges ---`);

        // Extraer todas las variables del flujo
        // 1) Escanear datos ORIGINALES (antes de transformar) para %VAR
        // 2) Escanear datos traducidos para {{VAR}} (campos de texto)
        // 3) Recolectar campos de nombre de variable de cada nodo traducido
        const varSet = new Set();
        const rePercent  = /%([A-Z_][A-Z0-9_.]*)/g;
        const reCurly    = /\{\{([A-Z_][A-Z0-9_.]*)\}\}/g;
        let vm;
        const rawStr = JSON.stringify(datosMap);
        while ((vm = rePercent.exec(rawStr))  !== null) varSet.add(vm[1]);
        const translStr = JSON.stringify(nodes.map(n => n.data));
        while ((vm = reCurly.exec(translStr)) !== null) varSet.add(vm[1]);
        // Campos estructurados que guardan nombre de variable (sin %)
        for (const n of nodes) {
          const d = n.data;
          if (n.type === "conditions")    (d.conditions || []).forEach(c => c.variable && varSet.add(c.variable));
          if (n.type === "setField")      d.variable && varSet.add(d.variable);
          if (n.type === "aiBot")         [d.outputVariable, d.disconnectVar, d.assistanceVar].forEach(v => v && varSet.add(v));
          if (n.type === "textMenu")      d.saveResponseTo && varSet.add(d.saveResponseTo);
          if (n.type === "loopControl")   d.variable && varSet.add(d.variable);
          if (n.type === "dbQuery")       d.outputVar && varSet.add(d.outputVar);
        }
        // Solo variables con nombre simple: letras, dígitos, guión bajo (sin @, ., [, ), etc.)
        const simpleVarRe = /^[A-Z_][A-Z0-9_]*$/;
        const knownDefaults = { "STS": "0", "PTI": "0", "CID": "" };
        const extractedVars = [...varSet]
          .filter(name => simpleVarRe.test(name))
          .sort()
          .map(name => ({
            id: `v_${name.toLowerCase()}`,
            name,
            defaultValue: knownDefaults[name] ?? "",
          }));
        L(`Variables extraídas: ${extractedVars.map(v => v.name).join(", ")}`);

        const output = { name: camName, nodes, edges, vars: extractedVars, lang: "es", v: "2.0", at: new Date().toISOString(), ...(groqEnabled ? { aiConfig: { ...groqConfig } } : {}) };

        // Descargar JSON convertido
        const jBlob = new Blob([JSON.stringify(output, null, 2)], { type: "application/json" });
        const ja = document.createElement("a");
        ja.href = URL.createObjectURL(jBlob);
        ja.download = `${camName.replace(/\s+/g, "_")}_converted.json`;
        ja.click();

        L(`✅ Convertido: ${nodes.length} nodos, ${edges.length} conexiones`, "ok");
        setConvDone(true);
        setCytMsg({ ok: true, text: `✅ Convertido: ${nodes.length} nodos, ${edges.length} conexiones. JSON descargado.` });
      })
      .catch(err => {
        L(`EXCEPTION: ${err.message}`, "error");
        setConvDone(true);
        setCytMsg({ ok: false, text: `❌ ${err.message}` });
      });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setBots(await listBots(userId)); }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // ── Crear bot ──────────────────────────────────────────────
  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const bot = await createBot(userId, newName.trim(), newPhone.trim());
      setShowNew(false); setNewName(""); setNewPhone("");
      onOpenBot(bot); // abre directo al nuevo bot
    } catch (e) { setError(e.message); }
    finally { setCreating(false); }
  };

  // ── Guardar edición ────────────────────────────────────────
  const handleSaveEdit = async (botId) => {
    try {
      await updateBotMeta(botId, { name: editName.trim() || "Sin nombre", phone: editPhone.trim() });
      setEditingId(null);
      load();
    } catch (e) { setError(e.message); }
  };

  // ── Eliminar bot ───────────────────────────────────────────
  const handleDelete = async (botId) => {
    try {
      await deleteBot(botId);
      setDeletingId(null);
      load();
    } catch (e) { setError(e.message); }
  };

  const PROVIDER_COLOR = { aad: "#1e40af", github: "#161b22", dev: "#78350f" };

  return (
    <div style={{
      minHeight: "100vh", background: "linear-gradient(135deg, #0a0e1a 0%, #0f172a 60%, #1a1040 100%)",
      fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#e2e8f0",
    }}>

      {/* ── Top bar ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 24px", background: "rgba(15,23,42,0.9)", backdropFilter: "blur(12px)",
        borderBottom: "1px solid rgba(255,255,255,0.06)", position: "sticky", top: 0, zIndex: 100,
      }}>
        {/* Logo */}
        <svg viewBox="0 0 220 52" width="140" height="33" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="dg1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a855f7"/><stop offset="100%" stopColor="#7c3aed"/>
            </linearGradient>
            <linearGradient id="dg2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#a855f7"/><stop offset="100%" stopColor="#818cf8"/>
            </linearGradient>
          </defs>
          <circle cx="10" cy="26" r="4.5" fill="url(#dg1)"/>
          <circle cx="28" cy="10" r="5.5" fill="url(#dg1)"/>
          <circle cx="28" cy="42" r="3.5" fill="#7c3aed" opacity="0.7"/>
          <line x1="13.5" y1="23.5" x2="24" y2="13" stroke="url(#dg1)" strokeWidth="1.8" strokeLinecap="round"/>
          <line x1="13.5" y1="28" x2="25" y2="39.5" stroke="#7c3aed" strokeWidth="1.4" strokeLinecap="round" opacity="0.7"/>
          <line x1="28" y1="15.5" x2="28" y2="38.5" stroke="#a855f7" strokeWidth="1.4" strokeLinecap="round" opacity="0.5"/>
          <text x="40" y="36" fontFamily="'Segoe UI', system-ui, sans-serif" fontWeight="800" fontSize="30" fill="url(#dg2)" letterSpacing="-0.5">CYT</text>
          <text x="41" y="48" fontFamily="'Segoe UI', system-ui, sans-serif" fontWeight="500" fontSize="9.5" fill="#64748b" letterSpacing="2.5">COMUNICACIONES</text>
        </svg>

        {/* Usuario */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 30, height: 30, borderRadius: "50%",
            background: PROVIDER_COLOR[provider] || "#1e293b",
            border: "1px solid #334155", display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 12, fontWeight: 700,
          }}>{avatarLetter}</div>
          <span style={{ fontSize: 12, color: "#94a3b8" }}>{displayName}</span>
          <button onClick={onLogout} style={{
            padding: "5px 12px", borderRadius: 6, border: "1px solid #334155",
            background: "transparent", color: "#64748b", fontSize: 11, cursor: "pointer",
          }}>Salir</button>
        </div>
      </div>

      {/* ── Contenido principal ── */}
      <div style={{ maxWidth: 960, margin: "0 auto", padding: "40px 24px" }}>

        {/* Título + botón nuevo */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#f1f5f9" }}>Mis Bots</h1>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#475569" }}>
              {bots.length === 0 ? "Aún no tenés bots creados" : `${bots.length} bot${bots.length !== 1 ? "s" : ""}`}
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {/* Convertir Bot CyT */}
            <button onClick={() => cytFileRef.current?.click()} style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "10px 16px", borderRadius: 8, border: "1px solid #b45309",
              cursor: "pointer", background: "rgba(180,83,9,0.1)",
              color: "#fbbf24", fontSize: 13, fontWeight: 600,
            }} title="Seleccioná el ZIP exportado desde CyT para convertirlo al formato designer">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 2L2 11h10L7 2z"/><path d="M7 6v3"/><circle cx="7" cy="10" r="0.5" fill="currentColor" stroke="none"/>
              </svg>
              Convertir Bot CyT
            </button>
            {/* Configuración de conversión */}
            <button onClick={() => setCytConfigOpen(v => !v)} title="Opciones de conversión" style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 36, height: 36, borderRadius: 8,
              border: `1px solid ${cytConfigOpen ? "#7c3aed" : "#334155"}`,
              background: cytConfigOpen ? "rgba(124,58,237,0.15)" : "transparent",
              color: cytConfigOpen ? "#a78bfa" : "#64748b",
              cursor: "pointer", fontSize: 16, flexShrink: 0,
            }}>⚙</button>
            <input ref={cytFileRef} type="file" accept=".zip" onChange={handleConvertCyt} style={{ display: "none" }} />

            {/* Nuevo Bot */}
            <button onClick={() => setShowNew(true)} style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 20px", borderRadius: 8, border: "none", cursor: "pointer",
              background: "linear-gradient(135deg, #7c3aed, #a855f7)",
              color: "#fff", fontSize: 13, fontWeight: 600,
              boxShadow: "0 4px 15px rgba(124,58,237,0.3)",
            }}>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M7 2v10M2 7h10"/>
              </svg>
              Nuevo Bot
            </button>
          </div>
        </div>

        {/* Panel de opciones de conversión CyT */}
        {cytConfigOpen && (
          <div style={{
            marginBottom: 16, padding: "16px 20px",
            background: "#0f172a", border: "1px solid #7c3aed",
            borderRadius: 10, boxShadow: "0 4px 20px rgba(124,58,237,0.15)",
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#a78bfa", marginBottom: 12 }}>
              ⚙ Opciones de conversión CyT
            </div>

            {/* Checkbox principal */}
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 14 }}>
              <input type="checkbox" checked={groqEnabled} onChange={e => setGroqEnabled(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: "#7c3aed", cursor: "pointer" }} />
              <span style={{ fontSize: 13, color: "#e2e8f0", fontWeight: 600 }}>
                💬 Conectar con agentes virtuales (Groq IA)
              </span>
            </label>
            <p style={{ margin: "0 0 14px 26px", fontSize: 11, color: "#64748b" }}>
              Al convertir, cada nodo "Transferir a Agente" recibirá un Agente Groq IA conectado automáticamente con la configuración indicada.
            </p>

            {/* Campos de config Groq, visibles solo si habilitado */}
            {groqEnabled && (
              <div style={{ marginLeft: 26, display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={styles.label}>API Key de Groq</label>
                    <input value={groqConfig.apiKey} onChange={e => setGroqConfig(c => ({ ...c, apiKey: e.target.value }))}
                      placeholder="gsk_..." style={styles.input} />
                  </div>
                  <div>
                    <label style={styles.label}>Modelo</label>
                    <select value={groqConfig.model} onChange={e => setGroqConfig(c => ({ ...c, model: e.target.value }))} style={styles.input}>
                      <option value="llama-3.1-8b-instant">llama-3.1-8b-instant (rápido)</option>
                      <option value="llama3-8b-8192">llama3-8b-8192</option>
                      <option value="llama3-70b-8192">llama3-70b-8192 (potente)</option>
                      <option value="mixtral-8x7b-32768">mixtral-8x7b-32768</option>
                      <option value="gemma2-9b-it">gemma2-9b-it</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={styles.label}>Prompt del sistema (personalidad del agente)</label>
                  <textarea value={groqConfig.systemPrompt}
                    onChange={e => setGroqConfig(c => ({ ...c, systemPrompt: e.target.value }))}
                    rows={3} placeholder="Sos un agente de atención al cliente. Respondé en español, de forma amable y concisa."
                    style={{ ...styles.input, resize: "vertical", fontFamily: "inherit", fontSize: 12 }} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={styles.label}>Palabra clave para salir del chat</label>
                    <input value={groqConfig.exitKeyword} onChange={e => setGroqConfig(c => ({ ...c, exitKeyword: e.target.value }))}
                      placeholder="salir" style={styles.input} />
                  </div>
                  <div>
                    <label style={styles.label}>Máx. tokens por respuesta</label>
                    <input type="number" min={50} max={4096} value={groqConfig.maxTokens}
                      onChange={e => setGroqConfig(c => ({ ...c, maxTokens: Number(e.target.value) }))}
                      style={styles.input} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Resultado conversión Bot CyT */}
        {cytMsg && (
          <div style={{
            padding: "12px 16px", borderRadius: 8, marginBottom: 20, fontSize: 13,
            background: cytMsg.ok ? "rgba(5,150,105,0.1)" : "rgba(127,29,29,0.3)",
            border: `1px solid ${cytMsg.ok ? "#059669" : "#991b1b"}`,
            color: cytMsg.ok ? "#6ee7b7" : "#fca5a5",
            display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
          }}>
            <span>{cytMsg.text}</span>
            <button onClick={() => setCytMsg(null)} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: "12px 16px", background: "#7f1d1d", borderRadius: 8, marginBottom: 20, fontSize: 13, color: "#fca5a5" }}>
            ❌ {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: 60, color: "#334155" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", border: "3px solid #1e293b", borderTopColor: "#7c3aed", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
            Cargando bots...
          </div>
        )}

        {/* Grid de bots */}
        {!loading && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            {bots.map(bot => (
              <BotCard
                key={bot.id}
                bot={bot}
                isEditing={editingId === bot.id}
                editName={editName}
                editPhone={editPhone}
                isDeleting={deletingId === bot.id}
                isOpening={openingId === bot.id}
                onOpen={() => handleOpen(bot)}
                onStartCopy={() => { setCopySource(bot); setCopyName(`${bot.name} (copia)`); setCopyPhone(bot.phone || ""); }}
                onStartEdit={() => { setEditingId(bot.id); setEditName(bot.name); setEditPhone(bot.phone || ""); }}
                onCancelEdit={() => setEditingId(null)}
                onSaveEdit={() => handleSaveEdit(bot.id)}
                onEditName={setEditName}
                onEditPhone={setEditPhone}
                onStartDelete={() => setDeletingId(bot.id)}
                onCancelDelete={() => setDeletingId(null)}
                onConfirmDelete={() => handleDelete(bot.id)}
                onConfigAI={() => handleOpenAiConfig(bot)}
              />
            ))}

            {/* Card vacía si no hay bots */}
            {bots.length === 0 && (
              <div style={{
                gridColumn: "1 / -1", textAlign: "center", padding: "60px 20px",
                border: "2px dashed #1e293b", borderRadius: 12, color: "#334155",
              }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
                <p style={{ margin: 0, fontSize: 14 }}>Creá tu primer bot con el botón "Nuevo Bot"</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modal: Duplicar bot ── */}
      {copySource && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ margin: "0 0 4px", fontSize: 16, color: "#f1f5f9" }}>Duplicar Bot</h3>
            <p style={{ margin: "0 0 20px", fontSize: 12, color: "#475569" }}>
              Copiando flujo de <strong style={{ color: "#a855f7" }}>{copySource.name}</strong>
            </p>

            <label style={styles.label}>Nombre del nuevo bot *</label>
            <input
              autoFocus
              value={copyName}
              onChange={e => setCopyName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleDuplicate()}
              placeholder="Ej: Bot Clínica (copia)..."
              style={styles.input}
            />

            <label style={{ ...styles.label, marginTop: 12 }}>Número de WhatsApp</label>
            <input
              value={copyPhone}
              onChange={e => setCopyPhone(e.target.value)}
              placeholder="+549111234567"
              style={styles.input}
            />

            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button onClick={handleDuplicate} disabled={copying || !copyName.trim()} style={{
                flex: 1, padding: "10px 0", borderRadius: 7, border: "none", cursor: "pointer",
                background: copyName.trim() ? "linear-gradient(135deg, #065f46, #059669)" : "#1e293b",
                color: copyName.trim() ? "#fff" : "#475569", fontSize: 13, fontWeight: 600,
              }}>
                {copying ? "Duplicando..." : "📋 Duplicar"}
              </button>
              <button onClick={() => { setCopySource(null); setCopyName(""); setCopyPhone(""); }} style={{
                padding: "10px 16px", borderRadius: 7, border: "1px solid #334155",
                background: "transparent", color: "#64748b", fontSize: 13, cursor: "pointer",
              }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Nuevo bot ── */}
      {showNew && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={{ margin: "0 0 20px", fontSize: 16, color: "#f1f5f9" }}>Nuevo Bot</h3>

            <label style={styles.label}>Nombre del bot *</label>
            <input
              autoFocus
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
              placeholder="Ej: Bot Clínica, Bot Ventas..."
              style={styles.input}
            />

            <label style={{ ...styles.label, marginTop: 12 }}>Número de WhatsApp</label>
            <input
              value={newPhone}
              onChange={e => setNewPhone(e.target.value)}
              placeholder="+549111234567"
              style={styles.input}
            />

            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button onClick={handleCreate} disabled={creating || !newName.trim()} style={{
                flex: 1, padding: "10px 0", borderRadius: 7, border: "none", cursor: "pointer",
                background: newName.trim() ? "linear-gradient(135deg, #7c3aed, #a855f7)" : "#1e293b",
                color: newName.trim() ? "#fff" : "#475569", fontSize: 13, fontWeight: 600,
              }}>
                {creating ? "Creando..." : "Crear Bot"}
              </button>
              <button onClick={() => { setShowNew(false); setNewName(""); setNewPhone(""); }} style={{
                padding: "10px 16px", borderRadius: 7, border: "1px solid #334155",
                background: "transparent", color: "#64748b", fontSize: 13, cursor: "pointer",
              }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de conversión en tiempo real ── */}
      {/* Modal configuración IA por bot */}
      {aiConfigBot && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#111827", border:"1px solid #4c1d95", borderRadius:14, padding:28, width:"100%", maxWidth:480, boxShadow:"0 25px 50px rgba(0,0,0,0.5)" }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#a78bfa", marginBottom:4 }}>🤖 Agente IA</div>
            <div style={{ fontSize:12, color:"#475569", marginBottom:20 }}>{aiConfigBot.name}</div>

            {aiConfigLoading ? (
              <div style={{ textAlign:"center", padding:32, color:"#475569" }}>Cargando...</div>
            ) : (<>
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                <div>
                  <label style={styles.label}>API Key de Groq</label>
                  <input value={aiConfigData.apiKey} onChange={e => setAiConfigData(c => ({ ...c, apiKey: e.target.value }))}
                    placeholder="gsk_..." style={styles.input} />
                </div>
                <div>
                  <label style={styles.label}>Modelo</label>
                  <select value={aiConfigData.model} onChange={e => setAiConfigData(c => ({ ...c, model: e.target.value }))} style={styles.input}>
                    <option value="llama-3.1-8b-instant">llama-3.1-8b-instant (rápido)</option>
                    <option value="llama3-8b-8192">llama3-8b-8192</option>
                    <option value="llama3-70b-8192">llama3-70b-8192 (potente)</option>
                    <option value="mixtral-8x7b-32768">mixtral-8x7b-32768</option>
                    <option value="gemma2-9b-it">gemma2-9b-it</option>
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Prompt del sistema</label>
                  <textarea value={aiConfigData.systemPrompt} onChange={e => setAiConfigData(c => ({ ...c, systemPrompt: e.target.value }))}
                    rows={4} placeholder="Sos un agente de atención al cliente. Respondé en español, de forma amable y concisa."
                    style={{ ...styles.input, resize:"vertical", fontFamily:"inherit", fontSize:12 }} />
                </div>

                {/* Base de conocimiento */}
                <div>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                    <label style={styles.label}>Base de conocimiento</label>
                    <button onClick={() => docFileRef.current?.click()} style={{
                      padding:"3px 10px", borderRadius:5, border:"1px solid #4c1d95",
                      background:"rgba(124,58,237,0.15)", color:"#a78bfa",
                      cursor:"pointer", fontSize:11, fontWeight:600,
                    }}>+ Agregar archivo</button>
                  </div>
                  <input ref={docFileRef} type="file" accept=".txt,.md" multiple onChange={handleAddDocs} style={{ display:"none" }} />
                  {(aiConfigData.knowledgeDocs || []).length === 0 ? (
                    <div style={{ fontSize:11, color:"#475569", padding:"8px 10px", background:"rgba(255,255,255,0.03)", borderRadius:6, border:"1px dashed #1e293b" }}>
                      Sin documentos. Agregá archivos .txt o .md que el agente usará como contexto.
                    </div>
                  ) : (
                    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                      {(aiConfigData.knowledgeDocs || []).map((doc, i) => (
                        <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 10px", background:"rgba(255,255,255,0.04)", borderRadius:6, border:"1px solid #1e293b" }}>
                          <span style={{ fontSize:13 }}>📄</span>
                          <span style={{ flex:1, fontSize:11, color:"#cbd5e1", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{doc.name}</span>
                          <span style={{ fontSize:10, color:"#475569", flexShrink:0 }}>{Math.round(doc.content.length / 1000)}k chars</span>
                          <button onClick={() => handleRemoveDoc(i)} style={{ background:"none", border:"none", color:"#ef4444", cursor:"pointer", fontSize:14, lineHeight:1, padding:"0 2px" }}>×</button>
                        </div>
                      ))}
                      <div style={{ fontSize:10, color:"#475569", marginTop:2 }}>
                        Total: ~{Math.round((aiConfigData.knowledgeDocs || []).reduce((s, d) => s + d.content.length, 0) / 4)} tokens estimados
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                  <div>
                    <label style={styles.label}>Palabra clave para salir (usuario)</label>
                    <input value={aiConfigData.exitKeyword} onChange={e => setAiConfigData(c => ({ ...c, exitKeyword: e.target.value }))}
                      placeholder="salir" style={styles.input} />
                  </div>
                  <div>
                    <label style={styles.label}>Máx. tokens</label>
                    <input type="number" min={50} max={4096} value={aiConfigData.maxTokens}
                      onChange={e => setAiConfigData(c => ({ ...c, maxTokens: Number(e.target.value) }))} style={styles.input} />
                  </div>
                </div>
                <div>
                  <label style={styles.label}>Frase de corte del agente</label>
                  <input value={aiConfigData.endPhrase || ""} onChange={e => setAiConfigData(c => ({ ...c, endPhrase: e.target.value }))}
                    placeholder="ej: Corte de conexión" style={styles.input} />
                  <div style={{ fontSize:10, color:"#475569", marginTop:3 }}>
                    Cuando el agente incluya esta frase en su respuesta, la conversación termina y la respuesta se guarda en la variable de salida del nodo.
                  </div>
                </div>
              </div>
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:20 }}>
                <button onClick={() => setAiConfigBot(null)} style={{ padding:"8px 16px", borderRadius:7, border:"1px solid #334155", background:"transparent", color:"#64748b", cursor:"pointer", fontSize:12 }}>
                  Cancelar
                </button>
                <button onClick={handleSaveAiConfig} disabled={aiConfigSaving} style={{ padding:"8px 20px", borderRadius:7, border:"none", background:"linear-gradient(135deg,#7c3aed,#a855f7)", color:"#fff", cursor: aiConfigSaving ? "default":"pointer", fontSize:12, fontWeight:600, opacity: aiConfigSaving ? 0.7 : 1 }}>
                  {aiConfigSaving ? "Guardando..." : "Guardar"}
                </button>
              </div>
            </>)}
          </div>
        </div>
      )}

      {convOpen && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
          zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#0f172a", border: "1px solid #334155", borderRadius: 12,
            width: 700, maxWidth: "95vw", maxHeight: "80vh",
            display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.6)",
          }}>
            {/* Header */}
            <div style={{
              padding: "14px 20px", borderBottom: "1px solid #1e293b",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              {!convDone ? (
                <span style={{
                  width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
                  background: "#3b82f6", display: "inline-block",
                  animation: "convPulse 1s ease-in-out infinite",
                }} />
              ) : convLog.some(e => e.type === "error") ? (
                <span style={{ color: "#ef4444", fontSize: 18, lineHeight: 1 }}>✗</span>
              ) : (
                <span style={{ color: "#22c55e", fontSize: 18, lineHeight: 1 }}>✓</span>
              )}
              <span style={{ color: "#e2e8f0", fontWeight: 700, fontSize: 14, flex: 1 }}>
                {convDone
                  ? convLog.some(e => e.type === "error") ? "Conversión con errores" : "Conversión completada"
                  : "Convirtiendo..."}
              </span>
              <span style={{ color: "#64748b", fontSize: 11 }}>
                {convLog.length} líneas
              </span>
            </div>

            {/* Log area */}
            <div ref={logScrollRef} style={{
              flex: 1, overflowY: "auto", padding: "12px 16px",
              fontFamily: "'Cascadia Code','Consolas','Courier New',monospace",
              fontSize: 11, lineHeight: 1.7, minHeight: 200,
            }}>
              {convLog.map((entry, i) => (
                <div key={i} style={{
                  color: {
                    error:   "#f87171",
                    warn:    "#fbbf24",
                    ok:      "#34d399",
                    section: "#818cf8",
                    info:    "#94a3b8",
                  }[entry.type] || "#94a3b8",
                  borderLeft: entry.type === "error" ? "3px solid #f87171"
                    : entry.type === "ok" ? "3px solid #34d399"
                    : "3px solid transparent",
                  paddingLeft: 8, marginBottom: 1,
                }}>
                  {entry.msg}
                </div>
              ))}
              {!convDone && (
                <div style={{ color: "#475569", fontStyle: "italic", marginTop: 4 }}>
                  procesando...
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{
              padding: "12px 20px", borderTop: "1px solid #1e293b",
              display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center",
            }}>
              {convLog.length > 0 && (
                <button onClick={downloadConvLog} style={{
                  padding: "7px 14px", borderRadius: 7, border: "1px solid #334155",
                  background: "transparent", color: "#94a3b8", cursor: "pointer", fontSize: 12,
                }}>
                  Descargar log
                </button>
              )}
              <button
                onClick={() => setConvOpen(false)}
                disabled={!convDone}
                style={{
                  padding: "7px 18px", borderRadius: 7, border: "none",
                  background: convDone ? "#6366f1" : "#334155",
                  color: convDone ? "#fff" : "#64748b",
                  cursor: convDone ? "pointer" : "not-allowed",
                  fontSize: 12, fontWeight: 600,
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
          <style>{`@keyframes convPulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }`}</style>
        </div>
      )}
    </div>
  );
}

// ── Card individual de bot ──────────────────────────────────
function BotCard({ bot, isEditing, editName, editPhone, isDeleting, isOpening,
  onOpen, onStartCopy, onStartEdit, onCancelEdit, onSaveEdit, onEditName, onEditPhone,
  onStartDelete, onCancelDelete, onConfirmDelete, onConfigAI }) {

  const updated = new Date(bot.updated_at).toLocaleDateString("es-AR", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
  });
  const isNew = (Date.now() - new Date(bot.created_at).getTime()) < 24 * 60 * 60 * 1000;

  return (
    <div style={{
      background: "#111827", border: "1px solid #1e293b", borderRadius: 12,
      padding: 20, display: "flex", flexDirection: "column", gap: 12,
      transition: "border-color 0.15s, box-shadow 0.15s",
      boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
      position: "relative",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = "#7c3aed"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(124,58,237,0.15)"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = "#1e293b"; e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.3)"; }}
    >
      {/* Badge Nuevo */}
      {isNew && (
        <div style={{
          position: "absolute", top: 12, right: 12,
          background: "linear-gradient(135deg, #059669, #10b981)",
          color: "#fff", fontSize: 9, fontWeight: 700,
          padding: "2px 7px", borderRadius: 20,
          letterSpacing: "0.8px", textTransform: "uppercase",
          boxShadow: "0 2px 8px rgba(16,185,129,0.4)",
        }}>Nuevo</div>
      )}

      {/* Header: ícono + nombre */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: "linear-gradient(135deg, #1e293b, #0f172a)",
          border: "1px solid #334155", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20,
        }}>🤖</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {isEditing ? (
            <>
              <input value={editName} onChange={e => onEditName(e.target.value)}
                style={{ ...styles.input, marginBottom: 6, fontSize: 13, fontWeight: 600 }} />
              <input value={editPhone} onChange={e => onEditPhone(e.target.value)}
                placeholder="+549..." style={{ ...styles.input, fontSize: 12 }} />
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {bot.name}
              </div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>
                {bot.phone || <span style={{ fontStyle: "italic" }}>Sin teléfono</span>}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Fecha */}
      <div style={{ fontSize: 10, color: "#334155" }}>
        Modificado: {updated}
      </div>

      {/* Confirmación de borrado */}
      {isDeleting && (
        <div style={{ background: "#7f1d1d22", border: "1px solid #991b1b", borderRadius: 7, padding: "10px 12px" }}>
          <p style={{ margin: "0 0 8px", fontSize: 12, color: "#fca5a5" }}>¿Eliminar este bot? Esta acción no se puede deshacer.</p>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={onConfirmDelete} style={{ flex: 1, padding: "6px 0", borderRadius: 5, border: "none", background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              Eliminar
            </button>
            <button onClick={onCancelDelete} style={{ flex: 1, padding: "6px 0", borderRadius: 5, border: "1px solid #334155", background: "transparent", color: "#64748b", fontSize: 11, cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Acciones */}
      {!isDeleting && (
        <div style={{ display: "flex", gap: 6 }}>
          {isEditing ? (
            <>
              <button onClick={onSaveEdit} style={{ flex: 1, padding: "7px 0", borderRadius: 6, border: "none", background: "#7c3aed", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                Guardar
              </button>
              <button onClick={onCancelEdit} style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #334155", background: "transparent", color: "#64748b", fontSize: 11, cursor: "pointer" }}>
                Cancelar
              </button>
            </>
          ) : (
            <>
              <button onClick={onOpen} disabled={isOpening} style={{ flex: 1, padding: "7px 0", borderRadius: 6, border: "none", background: "linear-gradient(135deg, #1e40af, #2563eb)", color: "#fff", fontSize: 11, fontWeight: 600, cursor: isOpening ? "default" : "pointer", opacity: isOpening ? 0.7 : 1 }}>
                {isOpening ? "Cargando..." : "Abrir"}
              </button>
              <button onClick={onConfigAI} style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #4c1d95", background: "rgba(124,58,237,0.1)", color: "#a78bfa", fontSize: 11, cursor: "pointer" }} title="Configurar agente IA">
                🤖
              </button>
              <button onClick={onStartCopy} style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #334155", background: "transparent", color: "#64748b", fontSize: 11, cursor: "pointer" }} title="Duplicar bot">
                📋
              </button>
              <button onClick={onStartEdit} style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #334155", background: "transparent", color: "#64748b", fontSize: 11, cursor: "pointer" }} title="Renombrar">
                ✏️
              </button>
              <button onClick={onStartDelete} style={{ padding: "7px 10px", borderRadius: 6, border: "1px solid #7f1d1d", background: "transparent", color: "#f87171", fontSize: 11, cursor: "pointer" }} title="Eliminar">
                🗑️
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Estilos compartidos ─────────────────────────────────────
const styles = {
  modalOverlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 500,
  },
  modal: {
    background: "#111827", border: "1px solid #1e293b", borderRadius: 14,
    padding: 28, width: "100%", maxWidth: 400,
    boxShadow: "0 25px 50px rgba(0,0,0,0.5)",
  },
  label: { fontSize: 11, color: "#64748b", display: "block", marginBottom: 4 },
  input: {
    width: "100%", background: "#1e293b", border: "1px solid #334155", borderRadius: 7,
    color: "#e2e8f0", padding: "8px 10px", fontSize: 13, outline: "none",
    boxSizing: "border-box",
  },
};

// Animación spinner
if (!document.getElementById("dashboard-styles")) {
  const s = document.createElement("style");
  s.id = "dashboard-styles";
  s.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(s);
}
